/**
 * Helper functions for migrating legacy task fields to supertag #todo format
 */

interface LegacyTaskFields {
  priority?: number
  note?: string
  due_date?: string | null
  due_time?: string | null
  duration?: number | null
  agile_status?: string | null
  completed?: boolean
  status?: string
  tags?: string[]
  project?: string
  assignedTo?: string
}

/**
 * Convert legacy task fields to structured_data format
 * Maps old direct fields → structured_data JSONB for #todo supertag
 */
export function convertLegacyTaskFieldsToStructuredData(
  fields: LegacyTaskFields
): Record<string, any> {
  const structuredData: Record<string, any> = {}

  // Priority: Map number (0-3) to select values
  const priorityMap = ['Low', 'Medium', 'High', 'Urgent']
  if (fields.priority !== undefined) {
    structuredData['Priority'] = priorityMap[fields.priority] || 'Medium'
  }

  // Notes: Use existing note field
  if (fields.note) {
    structuredData['Notes'] = fields.note
  }

  // Due Date: Use existing due_date
  if (fields.due_date) {
    structuredData['Due Date'] = fields.due_date
  }

  // Start Time: Use existing due_time
  if (fields.due_time) {
    structuredData['Start Time'] = fields.due_time
  }

  // Duration: Use existing duration (in hours)
  if (fields.duration !== null && fields.duration !== undefined) {
    structuredData['Duration'] = fields.duration
  }

  // Agile Status: Map from agile_status or tags
  if (fields.agile_status) {
    const agileStatusMap: Record<string, string> = {
      'now': 'Now',
      'next': 'Next',
      'later': 'Later'
    }
    structuredData['Agile Status'] = agileStatusMap[fields.agile_status] || fields.agile_status
  } else if (fields.tags) {
    // Check if agile status is in tags
    const agileTags = ['now', 'next', 'later']
    const agileTag = fields.tags.find(t => agileTags.includes(t.toLowerCase()))
    if (agileTag) {
      const agileStatusMap: Record<string, string> = {
        'now': 'Now',
        'next': 'Next',
        'later': 'Later'
      }
      structuredData['Agile Status'] = agileStatusMap[agileTag.toLowerCase()] || agileTag
    }
  }

  // Status: Map from status field or completed flag
  if (fields.completed) {
    structuredData['Status'] = 'Completed'
  } else if (fields.status) {
    const statusMap: Record<string, string> = {
      'todo': 'Active',
      'inProgress': 'In Progress',
      'done': 'Completed',
      'cancelled': 'Cancelled'
    }
    structuredData['Status'] = statusMap[fields.status] || 'Active'
  } else {
    structuredData['Status'] = 'Active'
  }

  // Project: TODO - Should be node ID relation, for now store as string
  if (fields.project) {
    structuredData['Project'] = fields.project
  }

  // Assignee: TODO - Should be node ID relation, for now store as string
  if (fields.assignedTo) {
    structuredData['Assignee'] = fields.assignedTo
  }

  // Default values for fields that don't have data
  if (!structuredData['Alert']) {
    structuredData['Alert'] = 'No alert'
  }
  if (!structuredData['Repeat']) {
    structuredData['Repeat'] = 'Never'
  }

  return structuredData
}

/**
 * Ensure tags array includes 'todo' tag
 */
export function ensureTodoTag(tags: string[] | undefined): string[] {
  const tagsArray = tags || []
  if (!tagsArray.includes('todo')) {
    return ['todo', ...tagsArray]
  }
  return tagsArray
}

/**
 * Convert legacy task object to node format with supertag
 */
export function convertLegacyTaskToNode(task: any) {
  return {
    ...task,
    tags: ensureTodoTag(task.tags),
    structured_data: convertLegacyTaskFieldsToStructuredData({
      priority: task.priority,
      note: task.note,
      due_date: task.due_date || task.dueDate,
      due_time: task.due_time || task.dueTime,
      duration: task.duration,
      agile_status: task.agile_status || task.agileStatus,
      completed: task.completed,
      status: task.status,
      tags: task.tags,
      project: task.project,
      assignedTo: task.assignedTo
    })
  }
}
